import { useState } from 'react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
} from '~/primitives/dropdown-menu';
import { cn } from '~/util';
import { useFieldContext } from '~/util/hooks/useFormContext';
import {
    FieldLabelWithError,
    FieldWithErrorProps,
} from './FieldLabelWithError';
import { useDisableFormContext } from '../DisableContext';
import { submissionStatus, SubmissionStatus } from '~/util/types/submissions';
import { SubmissionStatusTags } from '~/partials/Tags';
import { InteractableDataWrapper } from '~/partials/InteractableDataWrapper';
import { useScopes } from '~/components/Providers/AuthProvider';

export function SubmissionStatusField({
    placeholder,
    classname,
    allowNull = false,
    ...labelProps
}: {
    placeholder: string;
    classname?: string;
    allowNull?: boolean;
} & FieldWithErrorProps) {
    const field = useFieldContext<SubmissionStatus | null>();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const disabled = useDisableFormContext();

    const { hasScope } = useScopes();
    const canSeeUnderConsideration = hasScope('submission_reviewer_visible');

    return (
        <FieldLabelWithError<SubmissionStatus | null> {...labelProps}>
            <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
                <DropdownMenuTrigger disabled={disabled}>
                    <InteractableDataWrapper
                        id={field.name}
                        disabled={disabled}
                        open={dropdownOpen}
                        className={cn(
                            'bg-transparent hover:bg-transparent p-2 pl-0 w-fit',
                            field.state.value
                                ? 'text-secondary-foreground'
                                : 'text-secondary-light',
                            classname,
                            disabled && 'opacity-50 cursor-not-allowed',
                        )}
                        required={labelProps.required}
                    >
                        {field.state.value ? (
                            <SubmissionStatusTags item={field.state.value} />
                        ) : (
                            placeholder
                        )}
                    </InteractableDataWrapper>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                    <DropdownMenuRadioGroup
                        value={field.state.value ?? ''}
                        onValueChange={(value) => {
                            field.handleChange(
                                value === ''
                                    ? null
                                    : (value as SubmissionStatus),
                            );
                            setDropdownOpen(false);
                        }}
                    >
                        {allowNull && (
                            <DropdownMenuRadioItem
                                key="null"
                                value=""
                                className="text-lg cursor-pointer"
                            >
                                {placeholder}
                            </DropdownMenuRadioItem>
                        )}
                        {submissionStatus.map((status) => {
                            if (
                                !canSeeUnderConsideration &&
                                status == 'UnderConsideration'
                            ) {
                                return null;
                            }

                            return (
                                <DropdownMenuRadioItem
                                    key={status}
                                    value={status}
                                    className="text-lg cursor-pointer"
                                >
                                    <SubmissionStatusTags item={status} />
                                </DropdownMenuRadioItem>
                            );
                        })}
                    </DropdownMenuRadioGroup>
                </DropdownMenuContent>
            </DropdownMenu>
        </FieldLabelWithError>
    );
}
